import React, { useState } from 'react';
import { Button, Text, Textarea } from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import type { ChatWidgetProps } from '../../types/widgets';
import type { ApiButtonGroupWidget, ApiButtonConfig } from '../../types/api';

export const ButtonGroupWidget: React.FC<ChatWidgetProps<ApiButtonGroupWidget>> = ({
  data,
  isDisabled = false,
  onAction,
}) => {
  const { tokens, modeTokens } = useFluentTokens();
  const {
    widget_id,
    title,
    description,
    buttons,
    layout = 'horizontal',
  } = data;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [clickedButtonId, setClickedButtonId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');

  const handleButtonClick = async (button: ApiButtonConfig) => {
    if (isDisabled || hasSubmitted) return;
    
    if (onAction) {
      setIsSubmitting(true);
      setClickedButtonId(button.button_id);
      try {
        await onAction('button_action', {
          widget_id,
          button_action: button.action_value,
          selected_options: [],
        });
        setHasSubmitted(true);
      } finally {
        setIsSubmitting(false);
        setClickedButtonId(null);
      }
    }
  };

  const handleFeedbackSubmit = async () => {
    const trimmed = feedbackText.trim();
    if (!trimmed || isDisabled || hasSubmitted || !onAction) return;

    setIsSubmitting(true);
    try {
      await onAction('button_action', {
        widget_id,
        feedback_text: trimmed,
      });
      setHasSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const effectivelyDisabled = isDisabled || hasSubmitted;

  const getButtonAppearance = (style: string): 'primary' | 'secondary' | 'subtle' => {
    switch (style) {
      case 'success':
      case 'primary':
        return 'primary';
      case 'danger':
        return 'primary';
      case 'secondary':
      default:
        return 'secondary';
    }
  };

  const getButtonStyle = (button: ApiButtonConfig) => {
    const baseStyle: React.CSSProperties = {
      borderRadius: tokens.borderRadiusXXXXLarge,
      fontWeight: tokens.fontWeightSemibold,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      minWidth: '120px',
    };

    // Override disabled styles to maintain readability
    if (effectivelyDisabled) {
      return {
        ...baseStyle,
        cursor: 'not-allowed',
      };
    }

    // Custom colors based on style
    if (button.style === 'success') {
      return {
        ...baseStyle,
        backgroundColor: modeTokens.colorBrand80,
        color: '#ffffff',
        border: 'none',
      };
    } else if (button.style === 'danger') {
      return {
        ...baseStyle,
        backgroundColor: modeTokens.colorPaletteRedBackground3,
        color: '#ffffff',
        border: 'none',
      };
    }

    return baseStyle;
  };

  return (
    <Box
      style={{
        marginTop: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalL,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: modeTokens.colorNeutralBackground2,
        border: `0.5px solid ${modeTokens.colorNeutralStroke2}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
      }}
    >
      {title && (
        <Text
          as="h3"
          size={500}
          weight="semibold"
          style={{
            display: 'block',
            color: modeTokens.colorNeutralForeground1,
          }}
        >
          {title}
        </Text>
      )}

      {description && (
        <Text
          size={300}
          style={{
            display: 'block',
            color: modeTokens.colorNeutralForeground2,
          }}
        >
          {description}
        </Text>
      )}

      {/* Buttons */}
      <Box
        display="flex"
        style={{
          gap: tokens.spacingHorizontalM,
          flexDirection: layout === 'vertical' ? 'column' : 'row',
          alignItems: layout === 'vertical' ? 'stretch' : 'center',
        }}
      >
        {buttons.map((button) => (
          <Button
            key={button.button_id}
            appearance={getButtonAppearance(button.style)}
            disabled={isSubmitting || effectivelyDisabled}
            onClick={() => handleButtonClick(button)}
            style={getButtonStyle(button)}
          >
            {isSubmitting && clickedButtonId === button.button_id
              ? 'Processing...'
              : button.label}
          </Button>
        ))}
      </Box>

      {/* Freeform feedback input */}
      {!hasSubmitted && (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
          <Textarea
            placeholder="Or type feedback / follow-up question..."
            value={feedbackText}
            onChange={(_, d) => setFeedbackText(d.value)}
            disabled={isSubmitting || effectivelyDisabled}
            resize="vertical"
            style={{ minHeight: '48px' }}
          />
          {feedbackText.trim() && (
            <Button
              appearance="primary"
              size="small"
              disabled={isSubmitting || effectivelyDisabled}
              onClick={handleFeedbackSubmit}
              style={{ alignSelf: 'flex-start' }}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
